/**
 * InvoicePreviewContext — JEDYNY mechanizm otwierania podglądu faktury w PAX.
 *
 * Round 21 — architektoniczna unifikacja (audyt: UNIFIED_INVOICE_PREVIEW_ARCHITECTURE_AUDIT.md).
 * Zastępuje 4 osobne implementacje (Faktury/Inbox/Paczki/Przelewy).
 *
 * KONTRAKT:
 *   - Provider `<InvoicePreviewProvider>` renderowany raz w `MainLayout`.
 *   - Globalny Dialog + InvoiceDocumentPreview renderowane raz na całą aplikację.
 *   - `useInvoicePreview()` z contextu zwraca `{ openInvoice(inv), closeInvoice() }`.
 *   - `openInvoice(inv)` — akceptuje invoice-like obiekt (jak w InvoicesPage: pełen
 *     doc z listy `GET /api/invoices` z `ksef_number`, `seller_name`, `gross_amount`,
 *     `due_date`, `bank_account`, itd.).
 *
 * FLOW (identyczny dla WSZYSTKICH ekranów):
 *   openInvoice(inv)
 *     → GET /api/ksef/inbox/{ksef_number}       (jeśli ksef_number obecny)
 *     → POST /api/ksef/inbox/{ksef_number}/fetch-xml   (jeśli XML nie pobrany)
 *     → ponowny GET z parsed_tree FA(3)
 *     → InvoiceDocumentPreview(data)
 *
 * FALLBACK: gdy invoice nie ma `ksef_number` → `buildFallbackPreview(inv)` z
 * metadatami. Banner „Pełny XML nie jest dostępny." tylko wtedy.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import InvoiceDocumentPreview from "@/pages/invoices/InvoiceDocumentPreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoicePreviewContext = createContext(null);

const buildFallbackPreview = (inv) => ({
  invoice_number: inv.invoice_number || "",
  ksef_number: inv.ksef_number || "",
  invoice_date: inv.issue_date || "",
  seller_name: inv.seller_name || "",
  seller_nip: inv.seller_nip || "",
  seller_address: "",
  seller_email: "",
  seller_phone: "",
  buyer_name: inv.buyer_name || "",
  buyer_nip: inv.buyer_nip || "",
  buyer_address: "",
  currency: inv.currency || "PLN",
  gross_amount: inv.gross_amount || 0,
  net_amount: inv.net_amount || 0,
  vat_amount: inv.vat_amount || 0,
  invoice_type: "",
  due_date: inv.due_date || "",
  payment_method: "",
  bank_account: inv.bank_account || "",
  service_date: "",
  items: [],
  metadata_only: true,
  xml_fetch_error: "",
});

export const InvoicePreviewProvider = ({ children }) => {
  const [invoice, setInvoice] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingXml, setFetchingXml] = useState(false);
  const [extraFooterAction, setExtraFooterAction] = useState(null);

  const openInvoice = useCallback((inv, options = {}) => {
    if (!inv) return;
    setInvoice(inv);
    setData(null);
    setFetchingXml(false);
    setExtraFooterAction(options.extraFooterAction || null);
  }, []);

  const closeInvoice = useCallback(() => {
    setInvoice(null);
    setData(null);
    setFetchingXml(false);
    setExtraFooterAction(null);
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!invoice) return;
      if (!invoice.ksef_number) {
        setData(buildFallbackPreview(invoice));
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(
          `${API}/ksef/inbox/${encodeURIComponent(invoice.ksef_number)}`,
          { withCredentials: true },
        );
        if (!mounted) return;
        setData(res.data);
        if (res.data && !res.data.xml_fetched_at && !res.data.xml_fetch_error) {
          setFetchingXml(true);
          try {
            await axios.post(
              `${API}/ksef/inbox/${encodeURIComponent(invoice.ksef_number)}/fetch-xml`,
              {},
              { withCredentials: true, timeout: 90000 },
            );
            const enriched = await axios.get(
              `${API}/ksef/inbox/${encodeURIComponent(invoice.ksef_number)}`,
              { withCredentials: true },
            );
            if (mounted) setData(enriched.data);
          } catch (e) {
            const msg = e.response?.data?.detail || "Nie udało się pobrać pełnego dokumentu";
            if (mounted) {
              setData((prev) => prev ? { ...prev, xml_fetch_error: msg, metadata_only: true } : prev);
            }
          } finally {
            if (mounted) setFetchingXml(false);
          }
        }
      } catch (e) {
        if (mounted) setData(buildFallbackPreview(invoice));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [invoice]);

  return (
    <InvoicePreviewContext.Provider value={{ openInvoice, closeInvoice, invoice, data, loading, fetchingXml }}>
      {children}

      {/* GLOBALNY Dialog — renderowany raz na całą aplikację. */}
      <Dialog open={!!invoice} onOpenChange={(open) => { if (!open) closeInvoice(); }}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto p-0"
          data-testid="global-invoice-preview-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          <DialogHeader className="px-6 pt-6">
            <DialogTitle className="sr-only">Podgląd faktury</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="px-6 py-12 text-center text-gray-500" data-testid="global-invoice-preview-loading">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              Wczytywanie podglądu…
            </div>
          ) : (
            <InvoiceDocumentPreview data={data} fetchingXml={fetchingXml} />
          )}
          <DialogFooter className="px-6 pb-6 pt-2 gap-2">
            {extraFooterAction ? (
              <Button
                onClick={extraFooterAction.onClick}
                disabled={extraFooterAction.disabled}
                data-testid={extraFooterAction.testId || "global-invoice-preview-extra-btn"}
                className={extraFooterAction.className}
              >
                {extraFooterAction.label}
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={closeInvoice}
              data-testid="global-invoice-preview-close-btn"
            >
              Zamknij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InvoicePreviewContext.Provider>
  );
};

export const useInvoicePreview = () => {
  const ctx = useContext(InvoicePreviewContext);
  if (!ctx) {
    throw new Error("useInvoicePreview must be used within <InvoicePreviewProvider>");
  }
  return ctx;
};
